import * as React from "react";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import { useHistory, useRouteMatch } from "react-router";
import { Link, useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import { faChevronRight, faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { Keyboard } from "@capacitor/keyboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { Location } from "history";
import { noop, throttle } from "lodash";
import "tippy.js/animations/shift-away.css";

import AppContext from "~/common/AppContext";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import GlobalKeyboardListener from "~/common/GlobalKeyboardListener";
import type { NavbarAccountDropdownType } from "~/guest/components/navbarAccountDropdown/NavbarAccountDropdown";
import { GenericInvite } from "~/invite";
import NavbarAccountDropdown from "~/layout/NavbarAccountDropdown";
import { useBeaconSocket } from "~/lib/beacons";
import { inGoogleCalendar, inMSOutlook } from "~/lib/browserExtension";
import { useDailyUpdate } from "~/lib/dailyUpdate";
import useMedia from "~/lib/hooks/useMedia";
import useScript from "~/lib/hooks/useScript";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { CtrlOrCmd, inZoomMeeting, isDesktopApp, isMacos, isNative } from "~/lib/utils";
import AccountPanel from "~/nav/panels/Account";
import FeedbackPanel, { FeedbackContextProvider } from "~/nav/panels/Feedback";
import HomePanel from "~/nav/panels/Home";
import NotificationCenterPanel, { RefetchUnreadCountOnNewNotification } from "~/nav/panels/NotificationCenter";
import SearchPanel from "~/nav/panels/Search";
import SettingsPanel from "~/nav/panels/Settings";
import TemplatesPanel from "~/nav/panels/Templates";
import { GlobalSearchContext } from "~/search/GlobalSearchContext";
import SearchModal from "~/search/SearchModal";
import { Button, Tooltip, type TooltipProps } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { useModals } from "~/ui/modals";
import DismissibleTooltip from "~/ui/Tooltip/DismissibleTooltip";

import { useTourContext } from "../welcomeChecklist";
import DevMenu from "./DevMenu";
import { useLibraryNavigationFeatureFlagOn } from "./featureFlag";
import { ReactComponent as NotificationIconActive } from "./icons/notifications-filled.svg";
import { ReactComponent as NotificationIcon } from "./icons/notifications-outline.svg";
import { ReactComponent as ReferralIcon } from "./icons/referral-outline.svg";
import { ReactComponent as SearchIconActive } from "./icons/search-filled.svg";
import { ReactComponent as SearchIcon } from "./icons/search-outline.svg";
import MoreNavItem, { MORE_MENU_PANEL_KEYS } from "./MoreNavItem";
import { ActionItemsPanel } from "./panels/ActionItems";
import AnalyticsPanel from "./panels/Analytics/AnalyticsPanel";
import AskFellowPanel from "./panels/AskFellow";
import { emitter, HOME_PANEL_ITEM_REMOVED_EVENT_KEY } from "./panels/Home/Links/GroupsMenu";
import LibraryPanel from "./panels/Library/LibraryPanel";
import Meetings from "./panels/Meetings";
import ObjectivesPanel from "./panels/Okrs/ObjectivesPanel";
import ReturnToMeetingPrompt from "./ReturnToMeetingPrompt";
import { ReactComponent as TopUserStar } from "./topuser-star.svg";
import TopUserModal from "./TopUserModal";
import UpgradeLink from "./UpgradeLink";

import type { nav_CelloReferralFragment_viewer$key as CelloReferralFragment } from "~/__generated__/nav_CelloReferralFragment_viewer.graphql";
import type { nav_NotificationCenterButton_viewer$key as NotificationCenterButtonFragment } from "~/__generated__/nav_NotificationCenterButton_viewer.graphql";
import type { nav_viewer$key as NavQueryKey } from "~/__generated__/nav_viewer.graphql";

import styles from "./nav.module.css";

export const navStore = store.namespace("nav");
const LAST_PANEL_KEY = "lastPanelOpened";
const HOME_PANEL_REMOVED_ITEM_KEY = "HomePanel__removedHomePanelItem";

const MOBILE_MAX_WIDTH = 735; // must match media queries used in nav.less
/** Mode in which we slide panels on top of content, instead of pushing the content to the side */
export const SLIDEOVER_MAX_WIDTH = 1150; // 1150 is very much eyeballed

/** If we define these manually, rather than using the defaults, we can use :local CSS module classnames */
const cssTransitionClassNames = {
	enter: styles.transitionOpenFrom,
	enterActive: styles.transitionOpenTo,
	exit: styles.transitionCloseFrom,
	exitActive: styles.transitionCloseTo,
};

/** The width, in pixels, of the main nav */
let navWidth = 68;
/** The width, in pixels, of the nav panel */
let panelWidth = 280;
// Ask Fellow panel is wider than other panels
let askFellowPanelWidth = 380;

export interface NavOption {
	/** The displayed name of the section */
	label?: string;
	/** The icon to display - should use `currentColor` as its colour */
	icon: {
		active: React.ReactNode;
		inactive: React.ReactNode;
	};
	/** The id for which panel this is  */
	panelKey: PanelKey;
	/** Optional custom CSS class for the icon wrapper when active */
	customActiveIconClass?: string;
	/** Optional custom CSS class for the label when active */
	customActiveLabelClass?: string;
}

const loadingSpinner = <LoadingSpinner dimmer={false} delay={300} />;

/** A button in the primary section of the nav – importantly a _button_, and not a link */
const NavButton = React.memo(function NavButton(
	props: NavOption & {
		/** Whether the button is active -- that is, whether its panel is open */
		active?: boolean;
		/** A className to be applied to a green "beacon" dot, calling users' attention to this button */
		beaconClass?: string;
		/** Optional custom class for the icon wrapper when active */
		customActiveIconClass?: string;
		/** Optional custom class for the label when active */
		customActiveLabelClass?: string;
		/** The function to call when the button is clicked */
		onSelect: (key: PanelKey) => void;
	},
) {
	const { panelKey, onSelect } = props;
	const doClick = React.useCallback(() => {
		onSelect(panelKey);
	}, [onSelect, panelKey]);
	return (
		<button
			className={styles.navButton}
			data-active={!!props.active}
			onClick={doClick}
			data-test-id={`nav-button--${panelKey}`}
		>
			<div
				className={cx(styles.navButtonIconWrapper, { [props.customActiveIconClass ?? ""]: props.active })}
				aria-hidden
			>
				{props.active ? props.icon.active : props.icon.inactive}
				{props?.beaconClass && <div className={props.beaconClass} />}
			</div>
			{props.label && (
				<span className={cx(styles.navButtonLabel, { [props.customActiveLabelClass ?? ""]: props.active })}>
					{props.label}
				</span>
			)}
		</button>
	);
});

export function SecondaryNavButtonTooltip({ children, ...rest }: TooltipProps) {
	return (
		<Tooltip className={styles.navButtonTooltip} placement="right" offset={[0, -5]} animation="shift-away" {...rest}>
			{children}
		</Tooltip>
	);
}

function NotificationCenterButton({
	viewer: viewerKey,
	dispatchPanel,
	active,
}: {
	viewer: NotificationCenterButtonFragment;
	active: boolean;
	dispatchPanel: (key: PanelKey) => void;
}) {
	const [viewer, refetch] = useRefetchableFragment(
		graphql`
			fragment nav_NotificationCenterButton_viewer on Viewer
			@refetchable(queryName: "nav_NotificationCenterButton_viewer_refetchUnreadCount") {
				notificationCenter {
					id
					unreadNotificationCount
				}
			}
		`,
		viewerKey,
	);

	const dailyUpdateKey = useDailyUpdate();

	const openPanel = React.useCallback(() => dispatchPanel("notification-center"), [dispatchPanel]);

	React.useEffect(() => {
		if (dailyUpdateKey === 0) return;

		refetch({}, { fetchPolicy: "network-only" });
	}, [dailyUpdateKey, refetch]);

	// Feature is disabled
	if (!viewer.notificationCenter) return null;

	return (
		<SecondaryNavButtonTooltip content="Notifications">
			<button
				className={styles.navButton}
				data-active={active}
				onClick={openPanel}
				data-test-id="nav-button--notification-center"
			>
				{viewer.notificationCenter.unreadNotificationCount > 0 && (
					<span
						className={styles.notificationCount}
						data-unread-count={
							viewer.notificationCenter.unreadNotificationCount > 9
								? "9+"
								: viewer.notificationCenter.unreadNotificationCount
						}
					>
						&nbsp;
					</span>
				)}
				<div className={styles.navButtonIconWrapper} aria-hidden>
					{active ? <NotificationIconActive /> : <NotificationIcon />}
				</div>
			</button>
		</SecondaryNavButtonTooltip>
	);
}

function CelloReferral({ viewer: viewerKey }: { viewer: CelloReferralFragment }) {
	const [{ user, celloUserToken }] = useRefetchableFragment(
		graphql`
			fragment nav_CelloReferralFragment_viewer on Viewer
			@refetchable(queryName: "nav_CelloReferralFragment_viewer_refetchCelloUserToken") {
				celloUserToken
				user {
					firstName
					lastName
					fullName
					email
				}
			}
		`,
		viewerKey,
	);

	const loaded = useScript(window.CELLO_REFERRAL_URL);
	const [celloBooted, setCelloBooted] = React.useState(false);

	React.useEffect(() => {
		// window.cello is initialized in app.html
		// whereas window.Cello is created in cello.js
		if (!window.cello || !loaded || !celloUserToken || celloBooted) return;

		window.cello.cmd.push(cello => {
			cello.boot({
				productId: window.CELLO_PRODUCT_ID,
				token: celloUserToken,
				showOnBoot: true,
				hideDefaultLauncher: true,
				customLauncherSelector: "#celloButton",
				productUserDetails: {
					firstName: user.firstName,
					lastName: user.lastName,
					fullName: user.fullName,
					email: user.email,
				},
				announcement: {
					selector: "#celloButton",
					position: "right",
				},
			});
			setCelloBooted(true);
		});
	}, [loaded, user, celloUserToken, celloBooted]);

	return (
		<SecondaryNavButtonTooltip content="Referral Program">
			<div style={{ position: "relative" }}>
				<button
					// @ts-ignore
					cello="true"
					id="celloButton"
					className={cx(styles.navButton)}
					data-test-id="nav-button--cello-referral"
				>
					<div className={styles.navButtonIconWrapper}>
						<ReferralIcon data-cello-click="false" />
					</div>
				</button>
			</div>
		</SecondaryNavButtonTooltip>
	);
}

const SearchButton = React.forwardRef<
	HTMLAnchorElement,
	{ openQuickSearch: React.MouseEventHandler<HTMLAnchorElement> }
>(function SearchButton({ openQuickSearch }, ref) {
	const isMatching = Boolean(useRouteMatch("/search/"));

	return (
		<Link
			ref={ref}
			to="/search/"
			className={styles.navButton}
			data-active={isMatching}
			data-test-id="search-navbar-link"
			onClick={openQuickSearch}
		>
			<div className={styles.navButtonIconWrapper}>{isMatching ? <SearchIconActive /> : <SearchIcon />}</div>
		</Link>
	);
});

type NavAction = PanelKey | "toggle" | "open" | "close";

const NAV_PANEL_DISPATCH_EVENT = "fellow-nav-panel-dispatch";

type AnalyticsEvent = { event: string; properties: object; options: object };

export class NavPanelDispatchEvent extends CustomEvent<{
	key: NavAction;
	doNotClose?: boolean;
	analyticsEvent?: AnalyticsEvent;
}> {
	constructor(key: NavAction, doNotClose?: boolean, analyticsEvent?: AnalyticsEvent) {
		super(NAV_PANEL_DISPATCH_EVENT, { detail: { key, doNotClose, analyticsEvent } });
	}
}

declare global {
	interface WindowEventMap {
		[NAV_PANEL_DISPATCH_EVENT]: NavPanelDispatchEvent;
	}
}

export function useNavDispatchPanel() {
	return React.useCallback((key: NavAction, doNotClose?: boolean, analyticsEvent?: AnalyticsEvent) => {
		window.dispatchEvent(new NavPanelDispatchEvent(key, doNotClose, analyticsEvent));
	}, []);
}

export function useNavPanelAboveContent(): boolean {
	return useMedia(`(max-width: ${SLIDEOVER_MAX_WIDTH}px)`);
}

/**
 * This is supposed to use whatever panel you last had opened, as
 * persisted in localStorage (todo), but has special cases, e.g.
 * for the pages on which panels are hacky (Templates & Settings)
 */
function getDefaultPanelState(location: Location<unknown>): PanelState {
	const { pathname, state } = location;
	const open = window.innerWidth >= SLIDEOVER_MAX_WIDTH;
	if (typeof state === "object" && state && "navPanel" in state && isPanelKey(state.navPanel)) {
		return { key: state.navPanel, open };
	} else if (pathname.startsWith("/account/")) {
		return { key: "settings", open: true };
	} else if (pathname.startsWith("/templates-section/")) {
		return { key: "templates", open: true };
	} else if (pathname.startsWith("/meetings/")) {
		// The zoom app wants to be able to just open directly to the meetings panel
		return { key: "meetings", open: open || inZoomMeeting };
	} else if (navStore.has(LAST_PANEL_KEY)) {
		return {
			key: navStore.get(LAST_PANEL_KEY),
			open,
		};
	}
	return { key: "home", open };
}

/** The reducer function used to update the panel state */
function panelReducer(state: PanelState, action: NavAction): PanelState {
	if (action === "toggle") {
		return { ...state, open: !state.open };
	} else if (action === "open") {
		return { ...state, open: true };
	} else if (action === "close") {
		return { ...state, open: false };
	} else if (state.open && action === state.key) {
		return { ...state, open: false };
	} else {
		// On cold load, we default to the last of the *primary* panels you had open
		// Only save primary panels to the last panel key
		const isPrimaryPanel =
			typeof action === "string" &&
			action !== "search" &&
			action !== "account" &&
			ALL_PANEL_KEYS.includes(action as PanelKey);

		if (isPrimaryPanel) {
			navStore.set(LAST_PANEL_KEY, action);
		}
		return { open: true, key: action };
	}
}

const queryFragment = graphql`
	fragment nav_viewer on Query {
		viewer {
			unreadBeacons
			user {
				id
				isTopUser
				isGuest
				internalAttributes {
					dateConfigured
				}
			}
			account {
				id
				name
				logo
				uri
			}
			...MoreNavItem_viewer
			...NavbarAccountDropdown_viewer
			...UpgradeLink_viewer
			...MeetingsPanel_viewer # TODO: make the Agenda itself handle loading this
			...nav_NotificationCenterButton_viewer
			...nav_CelloReferralFragment_viewer
			...TopUserModal_viewer
		}
	}
`;

interface NavProps {
	query: NavQueryKey;
	items: NavOption[];
	disableSearch?: boolean;
	disableAskCopilot?: boolean;
	showUpgradeLink?: boolean;
	hideInvite?: boolean;
	hideGuestBanner?: boolean;
	CustomNavbarDropdown?: React.FunctionComponent<NavbarAccountDropdownType>;
	customHomePanel?: JSX.Element;
	toggleMockIntercomBubble?: () => void;
}

const ALL_PANEL_KEYS = [
	...MORE_MENU_PANEL_KEYS,
	"home",
	"library",
	"meetings",
	"action-items",
	"search",
	"settings",
	"account",
	"notification-center",
	"ask-fellow",
] as const;

export type PanelKey = (typeof ALL_PANEL_KEYS)[number];

function isPanelKey(key: unknown): key is PanelKey {
	return typeof key === "string" && ALL_PANEL_KEYS.includes(key as PanelKey);
}

interface PanelState {
	/* Whether a panel is expanded */
	open: boolean;
	/* Which panel is currently, or was last, open */
	key: PanelKey;
}
interface NavContextType {
	dispatchPanel: React.Dispatch<PanelKey | "toggle" | "open" | "close">;
	panel: PanelState;
}

export const NavContext = React.createContext<NavContextType>({
	dispatchPanel: noop,
	panel: { open: false, key: "home" },
});
NavContext.displayName = "NavContext";

export default function Navbar({
	query: queryKey,
	items,
	disableSearch = false,
	disableAskCopilot = false,
	showUpgradeLink = false,
	hideInvite = true,
	hideGuestBanner = true,
	CustomNavbarDropdown: CustomNavbar,
	customHomePanel,
	toggleMockIntercomBubble,
}: NavProps) {
	const { activeTour } = useTourContext();
	const { viewer } = useFragment(queryFragment, queryKey);
	useBeaconSocket(viewer.user.id);

	const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
	const panelAboveContent = useNavPanelAboveContent();
	const canHover = useMedia("(hover: hover)");

	const appContext = React.useContext(AppContext);
	const { globalSearchBarRef, lastQuery: lastGlobalSearchQuery } = React.useContext(GlobalSearchContext);
	const location = useLocation();
	const history = useHistory();
	const openQuickSearch = React.useCallback(
		(e: Event | React.SyntheticEvent) => {
			if (e.defaultPrevented) return;
			e.preventDefault();
			appContext.setSidebar?.("search");
			dispatchAnalyticsTrack("Open jump to", { feature: "search", fromHotkey: e.type !== "click" });
		},
		[appContext],
	);
	const globalSearchHotkey = React.useCallback(
		(e: Event) => {
			if (e.defaultPrevented) return;
			e.preventDefault();
			if (location.pathname.startsWith("/search/") && globalSearchBarRef?.current)
				globalSearchBarRef.current.focusToEnd();
			else if (lastGlobalSearchQuery) history.push({ pathname: "/search/", search: lastGlobalSearchQuery });
			else openQuickSearch(e);
		},
		[globalSearchBarRef, history, lastGlobalSearchQuery, location.pathname, openQuickSearch],
	);

	const [keyboardUp, setKeyboardUp] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (isNative) {
			Keyboard.addListener("keyboardWillHide", () => {
				setKeyboardUp(false);
			});
			Keyboard.addListener("keyboardWillShow", () => {
				setKeyboardUp(true);
			});
		}
	}, []);

	const [panel, dispatchPanel] = React.useReducer(panelReducer, location, getDefaultPanelState);
	React.useLayoutEffect(() => {
		// The search panel is only for mobile mode – otherwise it's elsewhere, in a modal
		if (!isMobile && panel.open && panel.key === "search") dispatchPanel("home");
	}, [isMobile, panel.open, panel.key, dispatchPanel]);

	React.useLayoutEffect(() => {
		const { pathname } = window.location;
		// In mobile & tablet mode, the panel should automatically close whenever we navigate
		// Hopefully I haven't missed any case in which it should stay open
		if (panelAboveContent && !(inZoomMeeting && pathname == "/meetings/")) dispatchPanel("close");
	}, [location, panelAboveContent, dispatchPanel]);

	React.useEffect(() => {
		function onNavDispatchPanel(event: NavPanelDispatchEvent) {
			if (event.detail.doNotClose && panel.key === event.detail.key) return dispatchPanel("open");

			if (event.detail.analyticsEvent && event.detail.key !== panel.key) {
				dispatchAnalyticsTrack(
					event.detail.analyticsEvent.event,
					event.detail.analyticsEvent.properties,
					event.detail.analyticsEvent.options,
				);
			}

			dispatchPanel(event.detail.key);
		}

		window.addEventListener(NAV_PANEL_DISPATCH_EVENT, onNavDispatchPanel);
		return () => window.removeEventListener(NAV_PANEL_DISPATCH_EVENT, onNavDispatchPanel);
	}, [panel.key]);

	const [hoveringNear, setHoveringNear] = React.useState(false);
	const toggleButtonAlwaysVisible = (panelAboveContent || !canHover) && panel.open;

	// Wrapper for dispatchPanel to track analytics when opening ask-fellow
	const dispatchPanelWithAskFellowAnalytics = React.useCallback(
		(key: PanelKey | "toggle" | "open" | "close") => {
			if (panel.key !== key) {
				switch (key) {
					case "ask-fellow": {
						// keeping old event name "[Ask Copilot] Modal opened" on purpose
						dispatchAnalyticsTrack("[Ask Copilot] Modal opened", { source: "navbar" });
						break;
					}
					default: {
						dispatchAnalyticsTrack("[Navigation] Panel opened", {
							source: "navbar",
							panel: key,
							inCalendarWithExtension: inGoogleCalendar ? "google" : inMSOutlook ? "outlook" : undefined,
						});
						break;
					}
				}
			}
			dispatchPanel(key);
		},
		[dispatchPanel, panel],
	);

	const togglePanel = React.useCallback(
		() => dispatchPanelWithAskFellowAnalytics("toggle"),
		[dispatchPanelWithAskFellowAnalytics],
	);
	const closePanel = React.useCallback(
		() => dispatchPanelWithAskFellowAnalytics("close"),
		[dispatchPanelWithAskFellowAnalytics],
	);

	/* Whenever the mouse is near the panel (in Desktop mode), show the panel toggle. It's done
	 * this way as opposed to just having an invisible element and using `:hover` because the
	 * invisible element method would be in front of the cursor, preventing the user from
	 * clicking anything behind it, and getting occluded if anything was in front of it. This
	 * way is more manual, but better reflects what we want: it's based purely on the mouse's
	 * position, not on what element it's currently pointing at. */
	React.useLayoutEffect(() => {
		if (!toggleButtonAlwaysVisible && canHover && !isMobile) {
			const handleMouseMove = throttle((e: MouseEvent) => {
				let panelWidthCalc = panel.key === "ask-fellow" ? askFellowPanelWidth : panelWidth;
				const hoverThreshold = navWidth + (panel.open ? panelWidthCalc : 0) + 32;
				if (!hoveringNear && e.clientX <= hoverThreshold) {
					setHoveringNear(true);
				} else if (hoveringNear && e.clientX > hoverThreshold) {
					setHoveringNear(false);
				}
			}, 100);
			window.addEventListener("mousemove", handleMouseMove);

			const handleMouseOut = () => {
				if (hoveringNear) setHoveringNear(false);
			};
			document.documentElement.addEventListener("mouseleave", handleMouseOut);

			return () => {
				window.removeEventListener("mousemove", handleMouseMove);
				document.documentElement.removeEventListener("mouseleave", handleMouseOut);
			};
		} else {
			return void 0;
		}
	}, [panel.open, panel.key, hoveringNear, setHoveringNear, toggleButtonAlwaysVisible, canHover, isMobile]);

	/**
	 * In this state, the size of the app's main content area is reduced, so we
	 * use this to adjust media queries on the screen's width.
	 */
	const mainWidthIsReduced = panel.open && !panelAboveContent && !isMobile;
	React.useLayoutEffect(() => {
		document.documentElement.classList.toggle("Nav--ReducedMainWidth", mainWidthIsReduced);
		document.documentElement.classList.toggle("Nav--FullMainWidth", !mainWidthIsReduced);
	}, [mainWidthIsReduced]);

	const modals = useModals();

	const openInviteModal = React.useCallback(
		async function setInviteModalOpen() {
			return modals.open(({ close }) => (
				// eslint-disable-next-line camelcase
				<GenericInvite
					onClose={close}
					// eslint-disable-next-line camelcase
					analytics={{ ux_location: "nav__index" }}
				/>
			));
		},
		[modals],
	);

	const { unreadBeacons, user } = viewer;
	const openTopUser = React.useCallback(() => {
		modals.open(({ close }) => <TopUserModal onClose={close} viewer={viewer} />);
	}, [modals, viewer]);

	const contextValue = React.useMemo(
		() => ({ dispatchPanel: dispatchPanelWithAskFellowAnalytics, panel }),
		[panel, dispatchPanelWithAskFellowAnalytics],
	);

	const [itemRemovedFromHomePanel, setItemRemovedFromHomePanel] = React.useState(false);

	function handleRemoveItemFromHomePanel() {
		setItemRemovedFromHomePanel(true);
	}

	React.useEffect(() => {
		emitter.on(HOME_PANEL_ITEM_REMOVED_EVENT_KEY, handleRemoveItemFromHomePanel);
		return () => {
			emitter.removeListener(HOME_PANEL_ITEM_REMOVED_EVENT_KEY, handleRemoveItemFromHomePanel);
		};
	}, []);

	const { hasDismissed, dismiss } = React.useContext(DismissibleElementsContext);

	const dismissHomePanelRemovedTooltip = React.useCallback(() => {
		dismiss(HOME_PANEL_REMOVED_ITEM_KEY);
	}, [dismiss]);

	React.useEffect(() => {
		if (panel.key === "ask-fellow" && panel.open) {
			document.documentElement.style.setProperty("--nav-panel-width", `${askFellowPanelWidth}px`);
		} else {
			document.documentElement.style.removeProperty("--nav-panel-width");
		}
	}, [panel.key, panel.open]);

	const openAskFellow = React.useCallback(() => {
		dispatchPanel("ask-fellow");
	}, [dispatchPanel]);

	const showRemovedItemTooltip = itemRemovedFromHomePanel && !hasDismissed(HOME_PANEL_REMOVED_ITEM_KEY);

	const showAskFellow = !disableAskCopilot;
	const libraryNavigationFeatureFlagOn = useLibraryNavigationFeatureFlagOn();

	return (
		<nav className={cx(styles.outer, { [styles.keyboardIsUp]: keyboardUp })}>
			<NavContext.Provider value={contextValue}>
				<FeedbackContextProvider>
					<div className={cx(styles.main, { [styles.desktopMode]: isDesktopApp && isMacos })}>
						{!isMobile && !hideGuestBanner && <div className={styles.guestBanner}>Guest</div>}
						{!isMobile &&
							(!!CustomNavbar ? <CustomNavbar viewer={viewer} /> : <NavbarAccountDropdown viewer={viewer} />)}
						{!isMobile && showUpgradeLink && <UpgradeLink viewer={viewer} className={styles.upgradeButton} />}
						{!isMobile && !showUpgradeLink && user.isTopUser && (
							<div className={styles.navTopUserStars} onClick={openTopUser}>
								<TopUserStar className={cx(styles.navTopUserStar, styles.starOne)} />
								<TopUserStar className={cx(styles.navTopUserStar, styles.starTwo)} />
								<TopUserStar className={cx(styles.navTopUserStar, styles.starThree)} />
								<span className={styles.navButtonLabel}>Top User</span>
							</div>
						)}
						<ul className={styles.mainItemSection}>
							{items.map(item => (
								<li key={item.panelKey}>
									<NavButton
										{...item}
										active={panel.open && panel.key === item.panelKey}
										onSelect={dispatchPanelWithAskFellowAnalytics}
										beaconClass={unreadBeacons.includes(`Navbar__${item.panelKey}`) ? styles.navButtonBeacon : ""}
									/>
								</li>
							))}

							{!isMobile && libraryNavigationFeatureFlagOn && !user.isGuest && (
								<li>
									<MoreNavItem
										viewer={viewer}
										isActive={(MORE_MENU_PANEL_KEYS as Readonly<string[]>).includes(panel.key)}
										dispatchPanel={dispatchPanelWithAskFellowAnalytics}
									/>
								</li>
							)}
						</ul>
						{!isMobile && (
							<>
								<div className={styles.secondaryItemSection}>
									<CelloReferral viewer={viewer} />
									<NotificationCenterButton
										viewer={viewer}
										dispatchPanel={dispatchPanelWithAskFellowAnalytics}
										active={panel.open && panel.key === "notification-center"}
									/>
									{!disableSearch && (
										<>
											<GlobalKeyboardListener sequence="mod+k" onEvent={openQuickSearch} global />
											<GlobalKeyboardListener sequence="mod+g" onEvent={globalSearchHotkey} global />

											{showRemovedItemTooltip ? (
												<Tooltip
													placement="right-start"
													visible={showRemovedItemTooltip}
													interactive
													content={
														<DismissibleTooltip
															dismiss={dismissHomePanelRemovedTooltip}
															icon={faSearch}
															text="You can re-access your removed notes by searching for them."
														/>
													}
												>
													<SearchButton openQuickSearch={openQuickSearch} />
												</Tooltip>
											) : (
												<SecondaryNavButtonTooltip content={`Quick Search (${CtrlOrCmd}+K)`}>
													<SearchButton openQuickSearch={openQuickSearch} />
												</SecondaryNavButtonTooltip>
											)}
											<SearchModal
												open={appContext.getSidebar?.() === "search"}
												onClose={appContext.closeSidebar ?? noop}
											/>
										</>
									)}
								</div>

								{!hideInvite && (
									<Button
										variant="accent"
										size="normal"
										className={styles.inviteButton}
										onClick={openInviteModal}
										data-test-id="invite-new-user-button"
									>
										Invite
									</Button>
								)}
							</>
						)}
						{showAskFellow && <GlobalKeyboardListener sequence="mod+/" onEvent={openAskFellow} global />}
						{window.ENVIRONMENT !== "prod" && !isMobile && (
							<DevMenu toggleMockIntercomBubble={toggleMockIntercomBubble} />
						)}
					</div>
					<CSSTransition in={panel.open} timeout={400} classNames={cssTransitionClassNames}>
						<div
							data-tour-tooltip-target="side-panel"
							className={cx(styles.panelContainer, {
								[styles.open]: panel.open,
								[styles.aboveContent]: panelAboveContent && !isMobile,
								[styles.desktopMode]: isDesktopApp && isMacos,
							})}
						>
							{React.useMemo(() => {
								// Without this memo call, the inner panels rerender every time the mouse goes in and out
								// of the panel area (to show the collapse arrow)
								return (
									<>
										{
											{
												["home"]: <>{!!customHomePanel ? customHomePanel : <HomePanel />}</>,
												["library"]: (
													<React.Suspense fallback={loadingSpinner}>
														<LibraryPanel />
													</React.Suspense>
												),
												["meetings"]: (
													<React.Suspense fallback={loadingSpinner}>
														<Meetings
															viewer={viewer}
															resyncOnMount={false}
															visible={panel.open}
															isTourOngoing={Boolean(activeTour)}
														/>
													</React.Suspense>
												),
												["feedback"]: (
													<React.Suspense fallback={loadingSpinner}>
														<FeedbackPanel />
													</React.Suspense>
												),
												["action-items"]: (
													<React.Suspense fallback={loadingSpinner}>
														<ActionItemsPanel />
													</React.Suspense>
												),
												["settings"]: (
													<React.Suspense fallback={loadingSpinner}>
														<SettingsPanel collapse={noop} />
													</React.Suspense>
												),
												["templates"]: (
													<React.Suspense fallback={loadingSpinner}>
														<TemplatesPanel collapse={noop} />
													</React.Suspense>
												),
												["search"]: (
													<React.Suspense fallback={loadingSpinner}>
														<SearchPanel />
													</React.Suspense>
												),
												["account"]: (
													<React.Suspense fallback={loadingSpinner}>
														<AccountPanel toggleMockIntercomBubble={toggleMockIntercomBubble} />
													</React.Suspense>
												),
												["notification-center"]: <NotificationCenterPanel />,
												["objectives"]: (
													<React.Suspense fallback={loadingSpinner}>
														<ObjectivesPanel />
													</React.Suspense>
												),
												["analytics"]: (
													<React.Suspense fallback={loadingSpinner}>
														<AnalyticsPanel />
													</React.Suspense>
												),
												["ask-fellow"]: (
													<React.Suspense fallback={loadingSpinner}>
														<AskFellowPanel />
													</React.Suspense>
												),
											}[panel.key]
										}
										{/* When the notification center is not open, render this
										component which will keep the unreadNotificationCount up to
										date */}
										{panel.key !== "notification-center" && <RefetchUnreadCountOnNewNotification userId={user.id} />}
									</>
								);
							}, [activeTour, customHomePanel, viewer, panel.open, panel.key, toggleMockIntercomBubble, user.id])}
							<ReturnToMeetingPrompt />
						</div>
					</CSSTransition>
					{isMobile ? (
						<CSSTransition in={panel.open} timeout={400} classNames={cssTransitionClassNames}>
							<button className={cx(styles.mobileCloseButton, { [styles.visible]: panel.open })} onClick={closePanel}>
								{/* Could replace this with finding the relevant panelKey in the items list and returning its label,
								but this is less expensive */}
								Close {panel.key.replace("-", " ")} panel
							</button>
						</CSSTransition>
					) : (
						<button
							className={cx(styles.toggleButton, {
								[styles.visible]: toggleButtonAlwaysVisible || hoveringNear,
								[styles.rotated]: panel.open,
							})}
							title={panel.open ? "Hide panel" : "Show panel"}
							onClick={togglePanel}
							data-test-id="collapse-panel-button"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					)}
					{isMobile && (
						<div className={cx(styles.panelDimmer, { [styles.visible]: panel.open })} onClick={closePanel} />
					)}
				</FeedbackContextProvider>
			</NavContext.Provider>
		</nav>
	);
}
